import { BaseSyntheticEvent } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { UserCreateRequestDto, UserRoles, useUserCreateControllerCreateUserMutation } from '@shared/services/apiService/apiService';
import { UserRolesEnum } from '@shared/constants/auth.constants';

type FormValues = Omit<UserCreateRequestDto, 'companyId'>;

interface UseCreateUserParams {
  companyId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const useCreateUser = ({ companyId, onClose, onSuccess }: UseCreateUserParams) => {
  const { t } = useTranslation();
  const [createUser] = useUserCreateControllerCreateUserMutation();

  const { handleSubmit, reset, setError, clearErrors, control, formState } = useForm<FormValues>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      role: UserRolesEnum.ADMIN,
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const submitHandler = handleSubmit(async (data) => {
    try {
      await createUser({ userCreateRequestDto: { ...data, companyId } }).unwrap();
      onSuccess();
      handleClose();
    } catch (error: any) {
      const apiErrors = error?.data;
      if (apiErrors && typeof apiErrors === 'object') {
        Object.entries(apiErrors).forEach(([field, message]) => {
          setError(field as keyof FormValues, { message: message as string });
        });
      } else {
        setError('email', {
          message: t('superAdminMainPage.companies.messages.somethingWentWrong'),
        });
      }
    }
  });

  // Clears previous server-side errors before handleSubmit re-runs validation,
  // preventing stale setError state from blocking subsequent submit attempts.
  const onSubmit = (e: BaseSyntheticEvent) => {
    clearErrors();
    submitHandler(e);
  };

  return {
    control,
    formState,
    onSubmit,
    handleClose,
    roleOptions: [
      { label: UserRolesEnum.ADMIN, value: UserRolesEnum.ADMIN as UserRoles },
      { label: UserRolesEnum.VIEWER, value: UserRolesEnum.VIEWER as UserRoles },
    ],
  };
};
